import React, { useMemo, useState } from "react";
import { ArrowLeft, ArrowRight, CheckCircle2, ListChecks, PauseCircle, Replace, Search, SkipForward, Users, Wallet } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Checkbox } from "./ui/checkbox";
import { Input } from "./ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { fmtDate, fmtINR } from "../lib/api";

/**
 * Phase 194 — Import REVIEW screen (step 1.5 of the Import Payments wizard, main + third-party).
 * The import is PAUSED when a PIN in the file already has MANUAL receipts (entered in the app by staff). One card per PIN
 * compares the existing manual receipts with the incoming bulk-import rows; the admin ticks ONE action per PIN:
 *   Replace → every manual receipt of the PIN is soft-deleted ("Replaced by bulk import", restorable 30 days) and all file
 *             rows of the PIN are imported — logged in the Audit Trail (admin, time, PIN, receipt numbers).
 *   Skip    → the manual receipts stay; no row of the PIN is imported — each becomes a Flagged Payments record
 *             "Manually reviewed — skipped" (Mark reviewed / Import now later).
 * "Proceed with selected actions" stays disabled until EVERY PIN with a ticked row has a decision.
 */
export const REVIEW_TONE = "bg-indigo-50 text-indigo-800 border-indigo-200";
const PAGE = 20;

export const groupNeedsDecision = (g, selected) =>
  (g?.incoming?.receipts || []).some((x) => x.status !== "error" && selected.has(x.row_number));

export const pendingReviewPins = (groups, selected, decisions) =>
  (groups || []).filter((g) => groupNeedsDecision(g, selected) && !["replace", "skip"].includes(decisions?.[g.pin])).map((g) => g.pin);

const plural = (n, one, many = `${one}s`) => (n === 1 ? one : many);

function DecisionOption({ id, checked, onToggle, disabled, icon: Icon, title, body, tone, testId }) {
  const active = checked
    ? (tone === "replace" ? "border-amber-400 bg-amber-50 ring-1 ring-amber-300" : "border-sky-400 bg-sky-50 ring-1 ring-sky-300")
    : "border-border bg-background hover:bg-muted/40";
  return (
    <label htmlFor={id} data-testid={testId} data-checked={checked ? "true" : "false"}
      className={`flex items-start gap-3 rounded-lg border px-3 py-2.5 transition-colors scroll-mb-28 ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"} ${active}`}>
      <Checkbox id={id} checked={checked} disabled={disabled} onCheckedChange={() => onToggle()} className="mt-0.5" data-testid={`${testId}-checkbox`} />
      <span className="text-sm min-w-0">
        <b className={`inline-flex items-center gap-1.5 ${tone === "replace" ? "text-amber-900" : "text-sky-900"}`}><Icon className="h-3.5 w-3.5" aria-hidden="true" /> {title}</b>
        <span className="block text-xs text-muted-foreground mt-0.5">{body}</span>
      </span>
    </label>
  );
}

function ReceiptsTable({ kind, receipts, selected, total, count, testId }) {
  const existing = kind === "existing";
  return (
    <div className="rounded-lg border bg-background overflow-x-auto" data-testid={testId}>
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            {!existing && <TableHead className="h-8 text-[11px] w-12">Row</TableHead>}
            <TableHead className="h-8 text-[11px]">Receipt</TableHead>
            <TableHead className="h-8 text-[11px]">Date</TableHead>
            <TableHead className="h-8 text-[11px] text-right">Amount</TableHead>
            <TableHead className="h-8 text-[11px]">Mode</TableHead>
            {existing && <TableHead className="h-8 text-[11px]">Collected by</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {receipts.length === 0 && (
            <TableRow><TableCell colSpan={5} className="text-xs text-muted-foreground text-center py-3">None</TableCell></TableRow>
          )}
          {receipts.map((r, i) => {
            const unticked = !existing && r.status !== "error" && !selected.has(r.row_number);
            const err = !existing && r.status === "error";
            return (
              <TableRow key={existing ? r.payment_id || i : r.row_number} className={`${unticked || err ? "opacity-50" : ""}`}
                data-testid={existing ? `${testId}-receipt-${i}` : `${testId}-row-${r.row_number}`}>
                {!existing && <TableCell className="py-1.5 font-mono text-[11px] text-muted-foreground">{r.row_number}</TableCell>}
                <TableCell className="py-1.5 font-mono text-xs">
                  {(existing ? r.receipt_number : r.receipt) || "—"}
                  {err && <span className="block text-[10px] text-destructive font-sans">Locked — not imported</span>}
                  {unticked && <span className="block text-[10px] text-muted-foreground font-sans">Not ticked — left out</span>}
                  {!existing && r.cheque_no && <span className="block text-[10px] text-muted-foreground font-sans">Chq {r.cheque_no}</span>}
                </TableCell>
                <TableCell className="py-1.5 text-xs whitespace-nowrap">{r.payment_date ? fmtDate(r.payment_date) : "—"}</TableCell>
                <TableCell className="py-1.5 text-xs text-right tabular-nums font-medium">{r.amount != null ? fmtINR(r.amount) : "—"}</TableCell>
                <TableCell className="py-1.5 text-xs">{r.payment_mode || "—"}</TableCell>
                {existing && (
                  <TableCell className="py-1.5 text-xs">
                    {r.collected_by_name || "—"}
                    {r.collected_by_employee_id && <span className="text-muted-foreground"> ({r.collected_by_employee_id})</span>}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
          <TableRow className="bg-muted/40 hover:bg-muted/40 font-medium">
            <TableCell colSpan={existing ? 2 : 3} className="py-1.5 text-xs">Total · {count} {plural(count, "receipt")}</TableCell>
            <TableCell className="py-1.5 text-xs text-right tabular-nums" data-testid={`${testId}-total`}>{fmtINR(total)}</TableCell>
            <TableCell colSpan={existing ? 2 : 1} />
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
}

export function ReviewGroupCard({ group: g, decision, onDecide, selected, tid, readOnly = false }) {
  const needs = groupNeedsDecision(g, selected);
  const liveIn = (g.incoming?.receipts || []).filter((x) => x.status !== "error" && selected.has(x.row_number));
  const inTotal = liveIn.reduce((n, x) => n + (x.amount || 0), 0);
  const exCount = g.existing?.count || 0;
  const exTotal = g.existing?.total || 0;
  const diff = Math.round((inTotal - exTotal) * 100) / 100;
  const area = [g.zone, g.ward, g.mohalla].filter(Boolean).join(" › ");
  const pinKey = g.pin;
  return (
    <Card className={`shadow-sm scroll-mb-28 ${decision === "replace" ? "border-amber-300" : decision === "skip" ? "border-sky-300" : needs ? "border-indigo-200" : ""}`}
      data-testid={`${tid}-review-group-${pinKey}`} data-decision={decision || "pending"}>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <CardTitle className="text-sm font-heading flex items-center gap-2 flex-wrap">
              <span className="font-mono" data-testid={`${tid}-review-group-pin-${pinKey}`}>{g.pin}</span>
              <span className="text-muted-foreground font-normal text-xs">{g.owner_name || "—"}{g.house_no ? ` · House ${g.house_no}` : ""}</span>
            </CardTitle>
            {area && <p className="text-[11px] text-muted-foreground mt-0.5">{area}</p>}
          </div>
          <div className="flex items-center gap-2">
            {!needs ? (
              <Badge variant="outline" className="text-[11px] bg-muted text-muted-foreground" data-testid={`${tid}-review-group-badge-${pinKey}`}>No rows ticked · nothing imported</Badge>
            ) : decision === "replace" ? (
              <Badge variant="outline" className="text-[11px] bg-amber-100 text-amber-900 border-amber-300 gap-1" data-testid={`${tid}-review-group-badge-${pinKey}`}><Replace className="h-3 w-3" /> Replace</Badge>
            ) : decision === "skip" ? (
              <Badge variant="outline" className="text-[11px] bg-sky-100 text-sky-900 border-sky-300 gap-1" data-testid={`${tid}-review-group-badge-${pinKey}`}><SkipForward className="h-3 w-3" /> Skip</Badge>
            ) : (
              <Badge variant="outline" className={`text-[11px] ${REVIEW_TONE}`} data-testid={`${tid}-review-group-badge-${pinKey}`}>Decision needed</Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <p className="text-xs font-semibold flex items-center gap-1.5 text-foreground">
              <Users className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" /> Existing manual receipts
              <Badge variant="outline" className="text-[10px] tabular-nums" data-testid={`${tid}-review-existing-count-${pinKey}`}>{exCount} · {fmtINR(exTotal)}</Badge>
            </p>
            <ReceiptsTable kind="existing" receipts={g.existing?.receipts || []} selected={selected} total={exTotal} count={exCount} testId={`${tid}-review-existing-${pinKey}`} />
            {(g.existing?.collectors || []).length > 0 && (
              <p className="text-[11px] text-muted-foreground">Entered in the app by {g.existing.collectors.join(", ")}{g.existing.first_date ? ` · ${fmtDate(g.existing.first_date)}${g.existing.last_date && g.existing.last_date !== g.existing.first_date ? ` – ${fmtDate(g.existing.last_date)}` : ""}` : ""}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <p className="text-xs font-semibold flex items-center gap-1.5 text-foreground">
              <Wallet className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" /> Incoming bulk-import receipts
              <Badge variant="outline" className="text-[10px] tabular-nums bg-violet-50 text-violet-800 border-violet-200" data-testid={`${tid}-review-incoming-count-${pinKey}`}>{liveIn.length} · {fmtINR(inTotal)}</Badge>
            </p>
            <ReceiptsTable kind="incoming" receipts={g.incoming?.receipts || []} selected={selected} total={inTotal} count={liveIn.length} testId={`${tid}-review-incoming-${pinKey}`} />
            {needs && (
              <p className="text-[11px] tabular-nums" data-testid={`${tid}-review-diff-${pinKey}`}>
                <span className="text-muted-foreground">Difference (import − manual): </span>
                <b className={diff > 0 ? "text-emerald-700" : diff < 0 ? "text-destructive" : "text-muted-foreground"}>{diff > 0 ? "+" : ""}{fmtINR(diff)}</b>
                {diff === 0 && <span className="text-muted-foreground"> · same total</span>}
              </p>
            )}
          </div>
        </div>
        {needs && !readOnly && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid={`${tid}-review-actions-${pinKey}`}>
            <DecisionOption id={`${tid}-replace-${pinKey}`} checked={decision === "replace"} onToggle={() => onDecide(g.pin, decision === "replace" ? null : "replace")}
              icon={Replace} tone="replace" title={`Replace ${exCount} manual ${plural(exCount, "receipt")}`} testId={`${tid}-review-replace-${pinKey}`}
              body={`The ${exCount} manual ${plural(exCount, "receipt")} (${fmtINR(exTotal)}) move to Deleted receipts as "Replaced by bulk import" (restorable for 30 days) and all ${liveIn.length} import ${plural(liveIn.length, "receipt")} (${fmtINR(inTotal)}) ${plural(liveIn.length, "is", "are")} recorded. Logged in the Audit Trail.`} />
            <DecisionOption id={`${tid}-skip-${pinKey}`} checked={decision === "skip"} onToggle={() => onDecide(g.pin, decision === "skip" ? null : "skip")}
              icon={SkipForward} tone="skip" title="Skip — keep the manual receipts" testId={`${tid}-review-skip-${pinKey}`}
              body={`Nothing changes on this PIN. The ${liveIn.length} import ${plural(liveIn.length, "receipt")} ${plural(liveIn.length, "is", "are")} not recorded and ${plural(liveIn.length, "appears", "appear")} under Reports → Flagged Payments as "Manually reviewed — skipped" (Mark reviewed or Import now later).`} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function ImportReviewScreen({ groups = [], decisions = {}, onChange, selected, onProceed, onBack, busy = false, tid = "payment-import", filename, error }) {
  const [q, setQ] = useState("");
  const [show, setShow] = useState(PAGE);
  const [only, setOnly] = useState("all");    // all | pending | decided

  const needing = useMemo(() => groups.filter((g) => groupNeedsDecision(g, selected)), [groups, selected]);
  const pending = useMemo(() => pendingReviewPins(groups, selected, decisions), [groups, selected, decisions]);
  const nReplace = needing.filter((g) => decisions[g.pin] === "replace").length;
  const nSkip = needing.filter((g) => decisions[g.pin] === "skip").length;
  const decided = nReplace + nSkip;
  const exReceipts = needing.reduce((n, g) => n + (g.existing?.count || 0), 0);
  const exTotal = needing.reduce((n, g) => n + (g.existing?.total || 0), 0);
  const inLive = needing.flatMap((g) => (g.incoming?.receipts || []).filter((x) => x.status !== "error" && selected.has(x.row_number)));
  const inTotal = inLive.reduce((n, x) => n + (x.amount || 0), 0);
  const replaceReceipts = needing.filter((g) => decisions[g.pin] === "replace").reduce((n, g) => n + (g.existing?.count || 0), 0);
  const skipRows = needing.filter((g) => decisions[g.pin] === "skip").flatMap((g) => (g.incoming?.receipts || []).filter((x) => x.status !== "error" && selected.has(x.row_number))).length;
  const allDecided = pending.length === 0 && needing.length > 0;

  const visible = useMemo(() => {
    const s = q.trim().toLowerCase();
    return groups.filter((g) => {
      if (s && !(`${g.pin} ${g.owner_name || ""} ${g.ward || ""} ${g.zone || ""}`.toLowerCase().includes(s))) return false;
      const needs = groupNeedsDecision(g, selected);
      if (only === "pending") return needs && !decisions[g.pin];
      if (only === "decided") return needs && !!decisions[g.pin];
      return true;
    });
  }, [groups, q, only, selected, decisions]);

  const decide = (pin, value) => {
    const next = { ...decisions };
    if (value) next[pin] = value; else delete next[pin];
    onChange(next);
  };
  const setAll = (value) => {
    const next = { ...decisions };
    needing.forEach((g) => { if (value) next[g.pin] = value; else delete next[g.pin]; });
    onChange(next);
  };

  return (
    <div className="space-y-4" data-testid={`${tid}-review-screen`}>
      <Card className="shadow-sm border-indigo-200" data-testid={`${tid}-review-card`}>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <CardTitle className="text-base font-heading flex items-center gap-2 text-indigo-950">
                <PauseCircle className="h-5 w-5 text-indigo-700" aria-hidden="true" />
                Import paused — manual receipts exist on {needing.length} {plural(needing.length, "PIN")}
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1 max-w-3xl" data-testid={`${tid}-review-intro`}>
                {filename ? <><b>{filename}</b> · </> : null}
                These PINs already have receipts entered in the app by staff. Nothing is imported until you choose what happens on <b>each</b> PIN:
                <b> Replace</b> removes the manual receipts (soft-delete, reason <i>Replaced by bulk import</i>) and records all import receipts of the PIN;
                <b> Skip</b> keeps the manual receipts and sends the import receipts of the PIN to Flagged Payments as <i>Manually reviewed — skipped</i>.
                Matching is by PIN only — receipt numbers and amounts are shown for comparison.
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={onBack} disabled={busy} data-testid={`${tid}-review-back`}>
              <ArrowLeft className="h-4 w-4 mr-1" /> Back to preview
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className={`rounded-xl border p-3 text-center ${REVIEW_TONE}`}>
              <ListChecks className="h-4 w-4 mx-auto mb-1" />
              <p className="text-xl font-semibold tabular-nums" data-testid={`${tid}-review-stat-pins`}>{needing.length}</p>
              <p className="text-[11px]">{plural(needing.length, "PIN")} to review</p>
            </div>
            <div className="rounded-xl border bg-muted/40 p-3 text-center">
              <Users className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
              <p className="text-xl font-semibold tabular-nums" data-testid={`${tid}-review-stat-existing`}>{exReceipts}</p>
              <p className="text-[11px] text-muted-foreground">Manual {plural(exReceipts, "receipt")} · {fmtINR(exTotal)}</p>
            </div>
            <div className="rounded-xl border bg-violet-50 border-violet-200 p-3 text-center">
              <Wallet className="h-4 w-4 mx-auto mb-1 text-violet-700" />
              <p className="text-xl font-semibold tabular-nums text-violet-800" data-testid={`${tid}-review-stat-incoming`}>{inLive.length}</p>
              <p className="text-[11px] text-violet-800">Import {plural(inLive.length, "receipt")} · {fmtINR(inTotal)}</p>
            </div>
            <div className={`rounded-xl border p-3 text-center ${allDecided ? "bg-emerald-50 border-emerald-200" : "bg-background"}`}>
              <CheckCircle2 className={`h-4 w-4 mx-auto mb-1 ${allDecided ? "text-emerald-600" : "text-muted-foreground"}`} />
              <p className={`text-xl font-semibold tabular-nums ${allDecided ? "text-emerald-700" : ""}`} data-testid={`${tid}-review-stat-decided`}>{decided} / {needing.length}</p>
              <p className={`text-[11px] ${allDecided ? "text-emerald-700" : "text-muted-foreground"}`}>Decided · {nReplace} replace · {nSkip} skip</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2" data-testid={`${tid}-review-toolbar`}>
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => { setQ(e.target.value); setShow(PAGE); }} placeholder="Find PIN / owner" className="h-8 pl-8 w-56 text-xs" data-testid={`${tid}-review-search`} />
            </div>
            <div className="flex rounded-md border overflow-hidden text-xs" role="tablist" aria-label="Show">
              {[["all", `All (${groups.length})`], ["pending", `Pending (${pending.length})`], ["decided", `Decided (${decided})`]].map(([k, l]) => (
                <button key={k} type="button" role="tab" aria-selected={only === k} onClick={() => { setOnly(k); setShow(PAGE); }}
                  className={`px-2.5 py-1.5 transition-colors ${only === k ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`} data-testid={`${tid}-review-filter-${k}`}>{l}</button>
              ))}
            </div>
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" className="h-8 border-amber-300 text-amber-900 hover:bg-amber-50" onClick={() => setAll("replace")} disabled={busy || needing.length === 0} data-testid={`${tid}-review-replace-all`}>
                <Replace className="h-3.5 w-3.5 mr-1.5" /> Replace all
              </Button>
              <Button variant="outline" size="sm" className="h-8 border-sky-300 text-sky-900 hover:bg-sky-50" onClick={() => setAll("skip")} disabled={busy || needing.length === 0} data-testid={`${tid}-review-skip-all`}>
                <SkipForward className="h-3.5 w-3.5 mr-1.5" /> Skip all
              </Button>
              <Button variant="ghost" size="sm" className="h-8" onClick={() => setAll(null)} disabled={busy || decided === 0} data-testid={`${tid}-review-clear-all`}>Clear</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-3" data-testid={`${tid}-review-groups`}>
        {visible.length === 0 && (
          <Card className="shadow-sm"><CardContent className="py-8 text-center text-sm text-muted-foreground" data-testid={`${tid}-review-empty`}>No PINs match this filter.</CardContent></Card>
        )}
        {visible.slice(0, show).map((g) => (
          <ReviewGroupCard key={g.pin} group={g} decision={decisions[g.pin]} onDecide={decide} selected={selected} tid={tid} />
        ))}
        {visible.length > show && (
          <div className="text-center">
            <Button variant="outline" size="sm" onClick={() => setShow(show + PAGE)} data-testid={`${tid}-review-show-more`}>Show {Math.min(PAGE, visible.length - show)} more of {visible.length - show} remaining</Button>
          </div>
        )}
      </div>

      {error && (
        <Alert variant="destructive" data-testid={`${tid}-review-error`}>
          <AlertTitle>Import could not be applied</AlertTitle>
          <AlertDescription data-testid={`${tid}-review-error-message`}>{error}</AlertDescription>
        </Alert>
      )}

      <div className="sticky bottom-2 z-10 rounded-lg border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 shadow-md" data-testid={`${tid}-review-footer`}>
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
          <div className="text-sm" data-testid={`${tid}-review-progress`}>
            {allDecided ? (
              <span className="inline-flex items-center gap-1.5 text-emerald-700"><CheckCircle2 className="h-4 w-4" /> All {needing.length} {plural(needing.length, "PIN")} decided</span>
            ) : (
              <span><b className="tabular-nums">{decided}</b> of {needing.length} {plural(needing.length, "PIN")} decided — <span className="text-indigo-800">{pending.length} still {plural(pending.length, "needs", "need")} Replace or Skip</span></span>
            )}
            <span className="block text-xs text-muted-foreground mt-0.5">
              {nReplace > 0 && <>{nReplace} {plural(nReplace, "PIN")} replace · {replaceReceipts} manual {plural(replaceReceipts, "receipt")} to Deleted receipts</>}
              {nReplace > 0 && nSkip > 0 && " · "}
              {nSkip > 0 && <>{nSkip} {plural(nSkip, "PIN")} skipped · {skipRows} import {plural(skipRows, "row")} to Flagged Payments</>}
              {decided === 0 && "Tick Replace or Skip on every PIN above, or use Replace all / Skip all."}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onBack} disabled={busy} data-testid={`${tid}-review-cancel`}>Back to preview</Button>
            <Button onClick={onProceed} disabled={busy || !allDecided} data-testid={`${tid}-review-proceed`}
              title={!allDecided ? `Choose Replace or Skip for ${pending.length} more ${plural(pending.length, "PIN")} first` : undefined}>
              {busy ? "Applying…" : "Proceed with selected actions"} <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ImportReviewScreen;
